import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, LabelList } from 'recharts';
interface LineChartVisualProps {
  data: Array<{
    label: string;
    value: number;
  }>;
  xAxisLabel?: string;
  yAxisLabel?: string;
  lineColor?: string;
  hideValues?: boolean;
  valueDisplay?: 'tooltip' | 'on-line' | 'on-axis-line';
  mode?: 'stimulus' | 'support';
}
export function LineChartVisual({
  data,
  xAxisLabel = '',
  yAxisLabel = '',
  lineColor = '#6366f1',
  hideValues = true,
  valueDisplay,
  mode = 'stimulus'
}: LineChartVisualProps) {
  const effectiveHideValues = mode === 'support' ? false : hideValues;
  const effectiveValueDisplay = valueDisplay || (effectiveHideValues ? 'on-axis-line' : 'on-line');
  const CustomTooltip = ({
    active,
    payload
  }: {
    active?: boolean;
    payload?: Array<{
      payload: {
        label: string;
        value: number;
      };
    }>;
  }) => {
    if (!active || !payload?.[0]) return null;
    const item = payload[0].payload;
    return <div className="bg-white px-3 py-2 rounded-lg shadow-lg border border-slate-200 text-sm" data-id="element-12239">
        <span className="font-semibold text-slate-700" data-id="element-12240">{item.label}</span>
        {!effectiveHideValues && <span className="text-slate-500 ml-2" data-id="element-12241">{item.value}</span>}
      </div>;
  };
  const CrosshairCursor = (props: any) => {
    const {
      activeCoordinate,
      width,
      height,
      left,
      top
    } = props;
    if (!activeCoordinate) return null;
    const {
      x,
      y
    } = activeCoordinate;
    return <g data-id="element-12242">
        <line x1={x} y1={top} x2={x} y2={top + height} stroke="#94a3b8" strokeDasharray="4 4" data-id="element-12243" />
        <line x1={left} y1={y} x2={left + width} y2={y} stroke="#94a3b8" strokeDasharray="4 4" data-id="element-12244" />
      </g>;
  };
  return <div className="w-full max-w-3xl mx-auto flex flex-col items-center" data-id="element-12245">
      {/* Visually hidden table for screen readers */}
      <table className="sr-only" data-id="element-12246">
        <caption data-id="element-12247">
          Lijngrafiek data
          {xAxisLabel || yAxisLabel ? ` voor ${xAxisLabel} en ${yAxisLabel}` : ''}
        </caption>
        <thead data-id="element-12248">
          <tr data-id="element-12249">
            <th scope="col" data-id="element-12250">{xAxisLabel || 'Categorie'}</th>
            <th scope="col" data-id="element-12251">{yAxisLabel || 'Waarde'}</th>
          </tr>
        </thead>
        <tbody data-id="element-12252">
          {data.map((item, i) => <tr key={i} data-id="element-12253">
              <td data-id="element-12254">{item.label}</td>
              <td data-id="element-12255">{item.value}</td>
            </tr>)}
        </tbody>
      </table>

      <div className="w-full h-[240px] md:h-[340px] bg-white rounded-xl border border-slate-200 shadow-sm p-3 md:p-4" data-id="element-12256">
        <ResponsiveContainer width="100%" height="100%" data-id="element-12257">
          <LineChart data={data} margin={{
          top: 20,
          right: 30,
          left: 20,
          bottom: 40
        }} data-id="element-12258">
            <CartesianGrid strokeDasharray="3 3" vertical={false} data-id="element-12259" />
            <XAxis dataKey="label" interval={0} tick={{
            fill: '#64748b',
            fontSize: 12
          }} tickLine={false} axisLine={{
            stroke: '#cbd5e1'
          }} label={xAxisLabel ? {
            value: xAxisLabel,
            position: 'insideBottom',
            offset: -20,
            fill: '#475569',
            fontSize: 13,
            fontWeight: 600
          } : undefined} data-id="element-12260" />
            <YAxis tick={{
            fill: '#64748b',
            fontSize: 12
          }} tickLine={false} axisLine={false} label={yAxisLabel ? {
            value: yAxisLabel,
            angle: -90,
            position: 'insideLeft',
            fill: '#475569',
            fontSize: 14,
            fontWeight: 600
          } : undefined} data-id="element-12261" />
            <Tooltip content={<CustomTooltip data-id="element-12263" />} cursor={<CrosshairCursor data-id="element-12264" />} data-id="element-12262" />

            {effectiveValueDisplay === 'on-axis-line' && Array.from(new Set(data.map(d => d.value))).map((val, i) => <ReferenceLine key={`ref-${i}`} y={val} stroke="#94a3b8" strokeDasharray="3 3" opacity={0.5} data-id="element-12265" />)}

            <Line type="monotone" dataKey="value" stroke={lineColor} strokeWidth={3} dot={{
            fill: '#fff',
            stroke: lineColor,
            strokeWidth: 2,
            r: 5
          }} activeDot={{
            fill: '#4f46e5',
            stroke: '#312e81',
            strokeWidth: 2,
            r: 7
          }} data-id="element-12266">
              {effectiveValueDisplay === 'on-line' && <LabelList dataKey="value" position="top" fill="#475569" fontSize={12} fontWeight={600} offset={10} data-id="element-12267" />}
            </Line>
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>;
}